import { useState } from 'react'
import { ExternalLink, KeyRound } from 'lucide-react'
import { useAnswerAccessRequestMutation, useGetProjectAccessQuery } from '../../services/projectDataApi'
import { accessGuidance } from '../../lib/accessBlockers'
import { distinctAccessRequests } from '../../lib/accessRequests'
import type { AccessRequest } from '../../schemas/projectAccess'
import './chat.css'

function AccessRequestCard({ projectId, request }: { projectId: string; request: AccessRequest }) {
  const guidance = accessGuidance(request.provider)
  const [error, setError] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [answer, mutation] = useAnswerAccessRequestMutation()
  if (answered) return <p role="status">Access response sent. QA is rechecking this run.</p>
  return (
    <form className="chat-form" data-testid="chat-access-request" onSubmit={async event => {
      event.preventDefault(); setError(null)
      try {
        await answer({ projectId, requestId: request.id, value: '' }).unwrap()
        setAnswered(true)
      } catch { setError('Could not answer this request. Only the project owner or an admin can respond, and the run must still be waiting.') }
      finally { mutation.reset() }
    }}>
      <div className="chat-form__head"><KeyRound size={15} aria-hidden /><span className="chat-form__title">{guidance.title}</span></div>
      <p className="chat-form__hint">{request.detail}</p>
      <p className="chat-form__hint">Blocked page: <a href={request.url} target="_blank" rel="noreferrer">{new URL(request.url).host}</a></p>
      <p className="chat-form__hint">{guidance.instructions}</p>
      {guidance.docs && <p className="chat-form__hint">
        <a className="chat-form__guide-link" href={guidance.docs} target="_blank" rel="noreferrer">
          How to disable Cloudflare bot protection <ExternalLink size={14} aria-hidden />
        </a>
      </p>}
      <p className="chat-form__hint">Waiting until {new Date(request.expires_at).toLocaleTimeString()}.</p>
      {error && <p className="pchat__error" role="alert">{error}</p>}
      <div className="chat-form__actions"><button type="submit" className="chat-form__submit" disabled={mutation.isLoading}>
        {mutation.isLoading ? 'Sending…' : 'Protection updated — retry'}
      </button></div>
    </form>
  )
}

export default function ChatAccessRequests({ projectId }: { projectId: string }) {
  const query = useGetProjectAccessQuery(projectId, { pollingInterval: 5000, skipPollingIfUnfocused: true })
  return <>{distinctAccessRequests(query.currentData?.requests ?? []).map(request => <AccessRequestCard key={request.id} projectId={projectId} request={request} />)}</>
}
